// Routes from Pony's direction API (through GET /api/directions) for every path the map draws:
// the walk to a booked bike, the ride to a parking, the itinerary legs.
import type { Directions, DirectionsMode, Position } from "../shared/api";
import { metersBetween } from "../shared/parking";
import { api } from "./api";

/** ~10 m: a GPS fix moving within this doesn't ask for a new route. */
const key = (a: Position, b: Position, mode: DirectionsMode) =>
  [a.lat, a.lng, b.lat, b.lng].map((n) => n.toFixed(4)).join(",") + mode;

type Fetcher = (a: Position, b: Position, mode: DirectionsMode) => Promise<Directions>;

/** A bounded cache in front of `fetcher`; requests in flight are shared, failures not kept. */
export function createDirections(fetcher: Fetcher, max = 50) {
  const cache = new Map<string, Promise<Directions>>();
  return {
    get(a: Position, b: Position, mode: DirectionsMode): Promise<Directions> {
      const k = key(a, b, mode);
      const hit = cache.get(k);
      if (hit) return hit;
      const p = fetcher(a, b, mode);
      cache.set(k, p);
      p.catch(() => cache.delete(k));
      while (cache.size > max) cache.delete(cache.keys().next().value as string);
      return p;
    },
  };
}

export const directions = createDirections((a, b, mode) =>
  api.get<Directions>(`/directions?from=${a.lng},${a.lat}&to=${b.lng},${b.lat}&mode=${mode}`),
);

/** Metres of drift allowed before a live route (the rider moves) is asked again. */
export const REROUTE_METERS = 40;

/** A live route is asked again for a new destination, or once the start drifted from the last one. */
export function needsNewRoute(
  last: { from: Position; to: Position } | undefined,
  from: Position,
  to: Position,
): boolean {
  if (!last) return true;
  if (metersBetween(last.to, to) > 1) return true;
  return metersBetween(last.from, from) > REROUTE_METERS;
}
